/**
 * Bildschirmtastatur (U15d): Auf dem Handy verdeckt sie sonst Eingaben. Chromium und die Android-WebView verkleinern
 * mit `interactive-widget=resizes-content` (index.html) die Seite selbst; iOS (Safari, WebView) und Android mit
 * Edge-to-Edge tun das nicht. Hier wird aus `visualViewport` gemessen, wie viel die Tastatur vom Fenster verdeckt,
 * und als CSS-Variable `--keyboard-inset` (Pixel) bereitgestellt; fokussierte Felder werden in die Mitte gerückt.
 * Ohne `visualViewport` passiert nichts (Variable bleibt 0). Bei Pinch-Zoom (`scale` ≠ 1) gilt der Inset als 0.
 * `html[data-keyboard]` wird gesetzt, solange die Tastatur offen ist (auch wenn Android die Seite selbst verkleinert);
 * damit blendet das CSS die Tableiste aus. Felder in Containern mit `data-keep-scroll` (Kasse) werden nie gescrollt.
 */

/** Zur Messung nötiger Ausschnitt von `VisualViewport`. */
export interface ViewportLike {
  height: number;
  offsetTop: number;
  /** Pinch-Zoom-Faktor (1 = kein Zoom); fehlt er, gilt 1 */
  scale?: number;
}

/** Unterhalb dieser Höhe (px) gilt es nicht als Tastatur (Adressleiste, Rundungen). */
const MIN_INSET_PX = 80;
/** Ab so viel verlorener Fensterhöhe (px) gilt es bei fokussiertem Textfeld als Tastatur (Seite wird selbst verkleinert). */
const RESIZE_KEYBOARD_PX = 150;
/** Abstand (px) zum unteren Rand, ab dem ein Feld als verdeckt gilt */
const MARGIN_PX = 8;

/** Von der Tastatur verdeckte Höhe in ganzen Pixeln, 0 ohne Tastatur oder bei Pinch-Zoom. */
export function keyboardInset(viewport: ViewportLike, windowHeight: number): number {
  if (Math.abs((viewport.scale ?? 1) - 1) > 0.01) return 0;
  const hidden = windowHeight - viewport.height - viewport.offsetTop;
  return hidden >= MIN_INSET_PX ? Math.round(hidden) : 0;
}

const FIELD = 'input, textarea, select, [contenteditable="true"]';
const TEXT_FIELD =
  'textarea, [contenteditable="true"], input:not([type="checkbox"],[type="radio"],[type="button"],[type="submit"],[type="range"],[type="file"])';

/** Ist das Feld unter der Tastatur oder über dem sichtbaren Ausschnitt? */
function isCovered(target: HTMLElement, viewport: ViewportLike): boolean {
  const rect = target.getBoundingClientRect();
  return (
    rect.bottom > viewport.offsetTop + viewport.height - MARGIN_PX || rect.top < viewport.offsetTop
  );
}

/** Startet die Messung; gibt die Abmeldung zurück. Mehrfacher Aufruf ist unschädlich, wenn man abmeldet. */
export function startKeyboardInset(): () => void {
  if (typeof window === 'undefined' || !window.visualViewport) return () => undefined;
  const viewport = window.visualViewport;
  const root = document.documentElement;
  const baseHeight = window.innerHeight;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let frame: number | undefined;
  let last: number | undefined;
  let lastKeyboard: boolean | undefined;

  const scrollIfCovered = (target: Element | null): void => {
    if (!(target instanceof HTMLElement) || !target.matches(FIELD)) return;
    if (target.closest('[data-keep-scroll]') !== null) return;
    if (isCovered(target, viewport)) target.scrollIntoView({ block: 'nearest' });
  };

  const write = (): void => {
    frame = undefined;
    const inset = keyboardInset(viewport, window.innerHeight);
    const active = document.activeElement;
    const keyboard =
      inset > 0 ||
      (active instanceof HTMLElement &&
        active.matches(TEXT_FIELD) &&
        active.getAttribute('inputmode') !== 'none' &&
        baseHeight - window.innerHeight > RESIZE_KEYBOARD_PX);
    if (keyboard !== lastKeyboard) {
      lastKeyboard = keyboard;
      if (keyboard) root.setAttribute('data-keyboard', '');
      else root.removeAttribute('data-keyboard');
    }
    if (inset === last) return;
    const grew = last !== undefined && inset > last;
    last = inset;
    root.style.setProperty('--keyboard-inset', String(inset) + 'px');
    if (grew) scrollIfCovered(active);
  };
  // Scroll/Resize feuern in Serie: je Bild höchstens eine Messung (ohne rAF sofort)
  const update = (): void => {
    if (typeof requestAnimationFrame !== 'function') {
      write();
      return;
    }
    if (frame === undefined) frame = requestAnimationFrame(write);
  };
  const onFocus = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches(FIELD)) return;
    // Die Tastatur fährt erst nach dem Fokus aus: nach dem Größenwechsel nachrücken
    clearTimeout(timer);
    timer = setTimeout(() => {
      write();
      if (keyboardInset(viewport, window.innerHeight) > 0 && document.activeElement === target) {
        scrollIfCovered(target);
      }
    }, 300);
  };

  write();
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  document.addEventListener('focusin', onFocus);
  return () => {
    clearTimeout(timer);
    if (frame !== undefined && typeof cancelAnimationFrame === 'function')
      cancelAnimationFrame(frame);
    viewport.removeEventListener('resize', update);
    viewport.removeEventListener('scroll', update);
    document.removeEventListener('focusin', onFocus);
    root.style.removeProperty('--keyboard-inset');
    root.removeAttribute('data-keyboard');
  };
}
